// The Broker Portal's design tokens: the palette as MUI reads it, and the type
// scale, control heights and breakpoints both Tailwind and MUI read.
//
// The palette's single swap point is `src/index.css`: every `brk-*` utility
// reads a CSS variable there, once per scheme (BRK-S245). This file MIRRORS
// both schemes for the MUI theme, which needs concrete colours to derive its
// own (hover, contrast text). test/lib/themeTokens.test.ts fails the moment the
// two disagree, so a palette change is still one edit plus a red test, never a
// silent fork between Tailwind and MUI surfaces.
//
// BRK is customer-facing and keeps its OWN design: nothing here comes from the
// PMS admin theme.
//
// The `-fill` roles are colours a filled control shows under white text. By day
// they equal their text roles; by night the text roles are paler, because no
// single colour can carry white text AND read on a dark surface at WCAG AA
// (test/lib/themeContrast.test.ts).

const light = {
  bg: "#f2f1f1",
  panel: "#ffffff",
  border: "#e3e2e6",
  text: "#202024",
  dim: "#57565e",
  accent: "#6d3ceb",
  "accent-fill": "#6d3ceb",
  "accent-strong": "#34245c",
  "accent-strong-fill": "#34245c",
  violet: "#a047ff",
  ink: "#202024",
  danger: "#dc2626",
  "danger-fill": "#dc2626",
} as const;

const dark: Record<keyof typeof light, string> = {
  bg: "#141417",
  panel: "#202024",
  border: "#37363d",
  text: "#f2f1f1",
  dim: "#a8a7b0",
  accent: "#a98bff",
  "accent-fill": "#6d3ceb",
  "accent-strong": "#cdbfff",
  "accent-strong-fill": "#5a30d8",
  violet: "#b77bff",
  ink: "#202024",
  danger: "#f87171",
  "danger-fill": "#dc2626",
};

/** Day ("light") and Night ("dark"), keyed like `data-theme`. */
export const schemes = { light, dark } as const;

/** The Day palette, for code that has no scheme to follow. */
export const brk = light;

export type BrkColor = keyof typeof light;

/** The body font stack from src/index.css — Söhne first, then the system stack
 *  `font-display: swap` falls back to while the face loads. */
export const fontStack = [
  '"Söhne"',
  "ui-sans-serif",
  "system-ui",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  '"Helvetica Neue"',
  "Arial",
  "sans-serif",
].join(", ");

// ── Type and density (BRK-S261) ──────────────────────────────────────────────
//
// Nav, 2026-09-28: a 14px base for a professional density, on ONE scale. The
// app had rendered 22 sizes from no scale at all. Tailwind's `text-*` classes
// (tailwind.config.ts, via `tailwindFontSize()`) and MUI's typography variants
// (theme.ts) both read these, in rem, so a user's browser text size still
// scales them. docs/design-system.md says when to use each.

/** Size and line height in px, and the weight a style sets. */
export interface TypeStyle {
  size: number;
  lineHeight: number;
  weight: number;
}

export const typeScale = {
  /** Landing and Exchange heroes only. Halbfett, as the brand book sets its
   *  hero headlines (BRK-S276). */
  display: { size: 28, lineHeight: 34, weight: 600 },
  /** A price a card is about: the quote cards' and the quote popup's total
   *  cost (BRK-S347). Set with tabular figures. */
  figure: { size: 24, lineHeight: 32, weight: 600 },
  /** The page title: its one h1. */
  title: { size: 20, lineHeight: 28, weight: 600 },
  /** A section or card title. */
  heading: { size: 16, lineHeight: 24, weight: 600 },
  subheading: { size: 14, lineHeight: 20, weight: 600 },
  /** Text, table cells, and inputs from md. */
  body: { size: 14, lineHeight: 20, weight: 400 },
  /** Subtitles, context lines, and field labels (at 500). */
  meta: { size: 13, lineHeight: 18, weight: 400 },
  /** Helper and error text, table headers (at 500), chips. */
  caption: { size: 12, lineHeight: 16, weight: 400 },
  /** Numeric counters only; nothing else goes under 12px. */
  badge: { size: 11, lineHeight: 12, weight: 600 },
} as const satisfies Record<string, TypeStyle>;

export type TypeStyleName = keyof typeof typeScale;

/** The styles that set their own weight. The text styles don't, so a
 *  `font-medium` or an inherited weight stands. */
const WEIGHTED: readonly TypeStyleName[] = [
  "display",
  "figure",
  "title",
  "heading",
  "subheading",
  "badge",
];

/** px as rem, against the 16px root the html keeps. */
export function rem(px: number): string {
  return `${px / 16}rem`;
}

/** The scale as Tailwind's `fontSize` theme: `text-title` and the rest. */
export function tailwindFontSize(): Record<
  TypeStyleName,
  [string, { lineHeight: string; fontWeight?: string }]
> {
  const entries = (Object.keys(typeScale) as TypeStyleName[]).map((name) => {
    const { size, lineHeight, weight } = typeScale[name];
    const settings = WEIGHTED.includes(name)
      ? { lineHeight: rem(lineHeight), fontWeight: String(weight) }
      : { lineHeight: rem(lineHeight) };
    return [name, [rem(size), settings]] as const;
  });
  return Object.fromEntries(entries) as ReturnType<typeof tailwindFontSize>;
}

/** Control heights in px: desktop-dense, with touch targets under a coarse
 *  pointer and 16px field text below md, where iOS zooms otherwise. */
export const controlHeight = {
  buttonMd: 36,
  buttonSm: 32,
  input: 36,
  inputTouch: 40,
  touch: 44,
  tableHead: 36,
  tableRow: 40,
  tab: 40,
  navRow: 40,
} as const;

/** The breakpoints, in px: Tailwind's `screens` and MUI's `breakpoints` both
 *  read these, so `lg` is 1024px everywhere (MUI's own was 1200). */
export const screens = { sm: 640, md: 768, lg: 1024, xl: 1280 } as const;

/** The shell's fixed chrome, in px (BRK-S262; Nav, 2026-09-28: "The navbar
 *  height is too big. The footer has also more height."). src/index.css holds
 *  them as `--brk-header-h`, `--brk-footer-h` and `--brk-pager-h`, which the
 *  shell, the sidebar's bands, a list's dock and the focus scroll-padding
 *  read; test/lib/themeTokens.test.ts keeps the two equal. */
export const chromeHeight = { header: 56, footer: 40, pager: 44 } as const;

/** How wide a form or a record page runs, in px (`max-w-form`). A list fills
 *  the width instead (`isWidePath`). */
export const contentWidth = { form: 1040 } as const;
